/**
 * Research Ops, as one map: every page under /ops, the group it belongs to, the
 * one sentence that says what it is for, and where the help for it lives.
 *
 * WHY THIS EXISTS. Research Ops grew one button at a time — eleven of them in a
 * row on the dashboard, each page with its own "Back to Research Ops" link — and
 * Ryan, 2026-10-07: "so much info in different places … letter desk, offer desk,
 * financial desk, etc. not clear what everything is". Nothing was missing; what
 * was missing was the grouping and the sentence. This file is both, in one
 * place, so the tab bar (OpsLayout), every page's header (OpsPageHeader), the
 * help guide's anchors and the Help Center's directory cannot describe the same
 * page four different ways.
 *
 * GROUPS ARE JOBS, not tables. Talking to researchers (Messages), approving what
 * they found (Review), doing what an owner asked for (Owner requests — the four
 * desks), calling owners ourselves (Call Center), running the team (Team), and
 * looking back (Activity). A staff member knows which of those they came to do;
 * they do not know which table it is in.
 *
 * URLS DO NOT CHANGE. Every path here already existed (bar /ops/researchers,
 * which is new and only gives the roster a page of its own). Notification
 * emails, bells and bookmarks point at these paths and keep working.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity, ClipboardCheck, FileSignature, FileSpreadsheet, Handshake, Headset,
  LayoutDashboard, Mail, MapPin, MessagesSquare, ShieldAlert, StickyNote, Users,
  ClipboardList, Inbox, UserCog,
} from 'lucide-react';

export type OpsGroupId =
  | 'overview' | 'messages' | 'review' | 'requests' | 'calls' | 'team' | 'activity';

export type OpsPageId =
  | 'overview'
  | 'conversations' | 'notes'
  | 'review'
  | 'letters' | 'ndas' | 'financials' | 'offers'
  | 'call-center'
  | 'researchers' | 'assignments' | 'territories'
  | 'feed' | 'audit';

export interface OpsPage {
  id: OpsPageId;
  group: OpsGroupId;
  /** The tab label — short, because the group already says what kind of thing it is. */
  label: string;
  /** The page's own heading. Keeps the names the notification emails use ("Offer Desk"). */
  title: string;
  path: string;
  icon: LucideIcon;
  /** One sentence: what this page is for and what you do on it. Shown under the title. */
  description: string;
  /** Section id in the Research Ops guide (/help/guide/research-ops#<help>). */
  help: string;
  /** Deeper paths that belong to this page, e.g. a researcher's detail page. */
  prefixes?: string[];
}

export interface OpsGroup {
  id: OpsGroupId;
  label: string;
  icon: LucideIcon;
  /** What the group is for — the tab's tooltip and the Help Center's wording. */
  blurb: string;
  /** In display order. The first is where the group's tab goes. */
  pages: OpsPageId[];
}

export const OPS_PAGES: Record<OpsPageId, OpsPage> = {
  overview: {
    id: 'overview', group: 'overview', label: 'Overview', title: 'Research Ops',
    path: '/ops', icon: LayoutDashboard, help: 'overview',
    description:
      'Outside researchers call park owners for us. Start here: what is waiting on you, then how the books are moving.',
  },

  conversations: {
    id: 'conversations', group: 'messages', label: 'Conversations', title: 'Conversations',
    path: '/ops/chat', icon: MessagesSquare, help: 'messages',
    description:
      'One thread per researcher — every note, question, feedback item and offer message they have sent, and our replies. Answer here; they see it in the portal and by email.',
  },
  notes: {
    id: 'notes', group: 'messages', label: 'Note queue', title: 'Note queue',
    path: '/ops/notes', icon: StickyNote, help: 'note-queue',
    description:
      'The notes researchers leave on parks and leads, across everyone, as a to-do list. Reply, or mark one handled when it was settled another way.',
  },

  review: {
    id: 'review', group: 'review', label: 'Review', title: 'Review queue',
    path: '/ops/review', icon: ClipboardCheck, help: 'review',
    description:
      'Parks, corrections and development findings researchers have proposed. None of it reaches Flow until someone here accepts it. (Owner contacts they add are accepted in Contact Triage.)',
  },

  // OWNER REQUESTS, in the order a deal usually meets them: a letter to open the
  // door, an NDA before the seller shares anything, their books, then our offer.
  // Every one is raised by a researcher from the portal — the call card's More
  // tab, or the property page — and none of them sends anything on its own
  // except the financials upload link and the NDA, which go out as filed.
  letters: {
    id: 'letters', group: 'requests', label: 'Letters', title: 'Letter Desk',
    path: '/ops/letters', icon: Mail, help: 'letters',
    description:
      'Someone at a park — often the manager or the owner’s staff — asked a researcher for a letter from us. Claim it, draft it from the template, approve it and send it. Nothing goes out until one of us sends it.',
  },
  ndas: {
    id: 'ndas', group: 'requests', label: 'NDAs', title: 'NDA Desk',
    path: '/ops/ndas', icon: FileSignature, help: 'ndas',
    description:
      'Sellers who want our confidentiality agreement signed first. They e-sign it; we countersign here for Endeavor. Also where failed sends, change requests and paper-signed copies are dealt with.',
  },
  financials: {
    id: 'financials', group: 'requests', label: 'Financials', title: 'Financials Desk',
    path: '/ops/financials', icon: FileSpreadsheet, help: 'financials',
    description:
      'Sellers who agreed to send their books — T-12s and rent rolls. Flow emails them an upload link; watch for the files, check they reached SharePoint, and run Analyze.',
  },
  offers: {
    id: 'offers', group: 'requests', label: 'Offers', title: 'Offer Desk',
    path: '/ops/offers', icon: Handshake, help: 'offers',
    description:
      'Owners who told a researcher they would sell or want a number. Claim a request, plan who delivers the offer and by when, write it, mark it sent, and track the answer.',
  },

  'call-center': {
    id: 'call-center', group: 'calls', label: 'Call Center', title: 'Call Center',
    path: '/ops/call-center', icon: Headset, help: 'call-center',
    description:
      'Call owners yourself: a researcher’s book, one of their lists, or the whole research inventory, one card at a time. Numbers are checked against do-not-call lists before you can dial.',
  },

  researchers: {
    id: 'researchers', group: 'team', label: 'Researchers', title: 'Researchers',
    path: '/ops/researchers', icon: UserCog, help: 'researchers',
    prefixes: ['/ops/researchers/'],
    description:
      'Who has portal access, how full their book is, and when they last signed in. Open one to see their book and calls, release work, or view the portal as them.',
  },
  assignments: {
    id: 'assignments', group: 'team', label: 'Assignments', title: 'Assignments',
    path: '/ops/assignments', icon: ClipboardList, help: 'assignments',
    description:
      'Everything out with researchers — properties and development leads — filtered by stage and researcher. Every pipeline number on the overview opens here.',
  },
  territories: {
    id: 'territories', group: 'team', label: 'Territories', title: 'Territories',
    path: '/ops/territories', icon: MapPin, help: 'territories',
    description:
      'The areas researchers work, built from buy boxes or from states, counties, cities and ZIPs. Add researchers to a territory, then hand them properties or development leads from its pool.',
  },

  feed: {
    id: 'feed', group: 'activity', label: 'Researcher activity', title: 'Researcher activity',
    path: '/ops/feed', icon: Activity, help: 'researcher-activity',
    description:
      'What researchers did, park by park, over the period you pick — notes, calls, texts, emails, stage moves, edits and proposals — against the period before.',
  },
  audit: {
    id: 'audit', group: 'activity', label: 'Portal audit', title: 'Portal audit',
    path: '/ops/activity', icon: ShieldAlert, help: 'portal-audit',
    description:
      'The security log: every portal request with its IP address, denied attempts and unusual volume. Look here if an account may be misused.',
  },
};

export const OPS_GROUPS: OpsGroup[] = [
  {
    id: 'overview', label: 'Overview', icon: LayoutDashboard, pages: ['overview'],
    blurb: 'What is waiting on you, and how the books are moving.',
  },
  {
    id: 'messages', label: 'Messages', icon: MessagesSquare, pages: ['conversations', 'notes'],
    blurb: 'Everything researchers write to us, and our replies.',
  },
  {
    id: 'review', label: 'Review', icon: ClipboardCheck, pages: ['review'],
    blurb: 'Data researchers found, waiting for someone to accept it into Flow.',
  },
  {
    id: 'requests', label: 'Owner requests', icon: Inbox, pages: ['letters', 'ndas', 'financials', 'offers'],
    blurb: 'The four desks — letters, NDAs, financials and offers a researcher has asked us to handle for the people at a park.',
  },
  {
    id: 'calls', label: 'Call Center', icon: Headset, pages: ['call-center'],
    blurb: 'Call owners yourself.',
  },
  {
    id: 'team', label: 'Team', icon: Users, pages: ['researchers', 'assignments', 'territories'],
    blurb: 'Researchers, what is assigned to them, and the territories they work.',
  },
  {
    id: 'activity', label: 'Activity', icon: Activity, pages: ['feed', 'audit'],
    blurb: 'What researchers did, and the portal’s security log.',
  },
];

/** The page a path belongs to, if any. `/ops` matches only itself — every page
 *  is under it, so a prefix match there would light Overview everywhere. */
export function findOpsPage(pathname: string): OpsPage | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/ops') return OPS_PAGES.overview;
  for (const page of Object.values(OPS_PAGES)) {
    if (page.id === 'overview') continue;
    if (path === page.path) return page;
    if (page.prefixes?.some((p) => path.startsWith(p))) return page;
  }
  return undefined;
}

export function opsGroupOf(page: OpsPage): OpsGroup {
  return OPS_GROUPS.find((g) => g.id === page.group)!;
}

/** Where the "How this works" link on a page goes. */
export function opsHelpHref(page: OpsPage): string {
  return `/help/guide/research-ops#${page.help}`;
}
